import { ElementType, FC, ReactElement, useState } from 'react';
import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import { CbAvatar } from '../CbAvatar';
import GeoPin from '../Icons/GeoPin';
import BriefCase from '../Icons/BriefCase/BriefCase';
import { CbLink } from '../CbLink';
export interface CbProfileInformationProps {
  avatarSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'custom';
  badgeUrl?: string;
  description?: string;
  image?: string;
  jobTitle?: string;
  location?: string;
  name: string;
  titleAdornment?: ReactElement;
  titleHref?: string;
  titleAs?: ElementType;
}
interface DecoratedSubTextProps {
  text: string;
  icon: ReactElement;
}
const DecoratedSubText: FC<DecoratedSubTextProps> = ({
  text,
  icon
}) => {
  return <Flex marginRight={4} marginBottom={1} alignItems="center" data-sentry-element="Flex" data-sentry-component="DecoratedSubText" data-sentry-source-file="CbProfileInformation.tsx">
      {icon}
      <Text fontSize="sm" color="text.tertiary" fontWeight="semibold" data-sentry-element="Text" data-sentry-source-file="CbProfileInformation.tsx">
        {text}
      </Text>
    </Flex>;
};
interface DescriptionTextProps {
  numberOfLines: number;
  description?: string;
  handleSeeMore: (event: string) => void;
  seeText;
}
const DescriptionText: FC<DescriptionTextProps> = ({
  numberOfLines,
  description,
  handleSeeMore,
  seeText
}) => {
  return <>
      <Text fontSize="sm" color="text.secondary" noOfLines={numberOfLines} data-sentry-element="Text" data-sentry-source-file="CbProfileInformation.tsx">
        {description}
      </Text>
      {description && <Text as="button" fontSize="sm" color="spyro.600" fontWeight="semibold" onClick={() => handleSeeMore(seeText)}>
          {seeText}
        </Text>}
    </>;
};
export const CbProfileInformation: FC<CbProfileInformationProps> = ({
  avatarSize,
  badgeUrl,
  description,
  image,
  jobTitle,
  location,
  name,
  titleAdornment,
  titleAs,
  titleHref
}) => {
  const [numberOfLines, setNumberOfLines] = useState(1);
  const [seeText, setSeeText] = useState('see more');
  const isMobile = useBreakpointValue({
    base: true,
    md: false
  }, {
    ssr: false
  });
  const handleSeeMore = (event: string): void => {
    if (event === 'see more') {
      setNumberOfLines(100);
      setSeeText('see less');
    } else {
      setNumberOfLines(1);
      setSeeText('see more');
    }
  };
  return <>
      <Flex paddingY={4} paddingX={{
      base: 4,
      md: 8
    }} alignItems={numberOfLines > 1 || isMobile ? 'start' : 'center'} data-sentry-element="Flex" data-sentry-source-file="CbProfileInformation.tsx">
        <CbLink as={titleAs} _hover={{
        textDecoration: 'none'
      }} href={titleHref} data-sentry-element="CbLink" data-sentry-source-file="CbProfileInformation.tsx">
          <CbAvatar name={name} avatarSize={avatarSize} image={image} badgeUrl={badgeUrl} marginRight={4} data-sentry-element="CbAvatar" data-sentry-source-file="CbProfileInformation.tsx" />
        </CbLink>
        <Box data-sentry-element="Box" data-sentry-source-file="CbProfileInformation.tsx">
          <Flex alignItems="center" data-sentry-element="Flex" data-sentry-source-file="CbProfileInformation.tsx">
            <CbLink href={titleHref} as={titleAs} _hover={{
            textDecoration: 'none'
          }} fontFamily="codenext" fontWeight="bold" fontSize="md" color="text.primary" marginRight={2} data-sentry-element="CbLink" data-sentry-source-file="CbProfileInformation.tsx">
              {name}
            </CbLink>
            {titleAdornment}
          </Flex>
          <Flex marginY={1} direction={{
          base: 'column',
          md: 'row'
        }} data-sentry-element="Flex" data-sentry-source-file="CbProfileInformation.tsx">
            {jobTitle && <DecoratedSubText icon={<BriefCase marginRight={1} color="text.tertiary" />} text={jobTitle} />}
            {location && <DecoratedSubText icon={<GeoPin marginRight={1} color="text.tertiary" />} text={location} />}
          </Flex>
          {!isMobile && <DescriptionText numberOfLines={numberOfLines} seeText={description && description.length > 60 ? seeText : null} description={description} handleSeeMore={handleSeeMore} />}
        </Box>
      </Flex>
      {isMobile && <Box paddingX={4} marginBottom={4}>
          <DescriptionText numberOfLines={numberOfLines} seeText={description && description.length > 50 ? seeText : null} description={description} handleSeeMore={handleSeeMore} />
        </Box>}
    </>;
};